import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, IconButton, ListItemIcon, ListItemText,
  Menu, MenuItem, TextField, Tooltip, Typography, Stack, FormControl, InputLabel, Select
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import PublishIcon from '@mui/icons-material/Publish'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import UnpublishedIcon from '@mui/icons-material/Unpublished'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import AddIcon from '@mui/icons-material/Add'
import { fetchThemes, createTheme, deleteTheme, publishTheme, unpublishTheme } from '../../actions/themeActions'
import { ThemeApi } from '../../api/themeApi'
import toast from '../../utils/toast'
import useTranslation from '../../hooks/useTranslation'

const statusColors = {
  published: 'success',
  draft: 'warning',
  archived: 'default'
}

const getStatusLabel = (status, t) => {
  if (status === 'published') return t('labels.themes.status.published')
  if (status === 'draft') return t('labels.themes.status.draft')
  if (status === 'archived') return t('labels.themes.status.archived')
  return status
}

export default function ThemeList() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const { themes, loading } = useSelector(state => state.themes)
  const [createOpen, setCreateOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const BLANK_THEME = '__blank__'
  const [cloneFrom, setCloneFrom] = useState(BLANK_THEME)
  const [publishConfirm, setPublishConfirm] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [unpublishConfirm, setUnpublishConfirm] = useState(null)
  const [menuAnchor, setMenuAnchor] = useState({ el: null, themeId: null })

  const openMenu = (e, themeId) => setMenuAnchor({ el: e.currentTarget, themeId })
  const closeMenu = () => setMenuAnchor({ el: null, themeId: null })

  useEffect(() => {
    dispatch(fetchThemes())
  }, [dispatch])

  const handleCreate = async () => {
    try {
      const result = await dispatch(createTheme({ name: newName || t('labels.themes.untitled'), cloneFrom: cloneFrom === BLANK_THEME ? null : cloneFrom }))
      setCreateOpen(false)
      setNewName('')
      setCloneFrom(BLANK_THEME)
      if (result?.theme?.id) {
        history.push(`/admin/themes/${result.theme.id}/editor`)
      }
    } catch (err) {
      alert(err.response?.data?.message || t('labels.themes.failedToCreate'))
    }
  }

  const handlePublish = async (id) => {
    try {
      await dispatch(publishTheme(id))
      setPublishConfirm(null)
      dispatch(fetchThemes())
    } catch (err) {
      alert(t('labels.themes.failedToPublish'))
    }
  }

  const handleDelete = async (id) => {
    try {
      await dispatch(deleteTheme(id))
      setDeleteConfirm(null)
    } catch (err) {
      alert(t('labels.themes.failedToDelete'))
    }
  }

  const handleEditLive = async (id) => {
    try {
      const res = await ThemeApi.createEditDraft(id)
      const draftId = res?.data?.theme?.id
      if (draftId) {
        history.push(`/admin/themes/${draftId}/editor`)
      }
    } catch (err) {
      toast.error(t('labels.themes.failedToOpenEditDraft'))
    }
  }

  const handleUnpublish = async (id) => {
    try {
      await dispatch(unpublishTheme(id))
      setUnpublishConfirm(null)
      dispatch(fetchThemes())
    } catch (err) {
      alert(t('labels.themes.failedToUnpublish'))
    }
  }

  const published = themes.filter(t => t.status === 'published')
  const drafts = themes.filter(t => t.status === 'draft')
  const archived = themes.filter(t => t.status === 'archived')

  const handleCloseCreateDialog = () => {
    setCreateOpen(false)
    setCloneFrom(BLANK_THEME)
    setNewName('')
  }

  const renderThemeCard = (theme) => (
    <Card key={theme.id} variant="outlined">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6">{theme.name}</Typography>
              <Chip
                label={getStatusLabel(theme.status, t)}
                color={statusColors[theme.status]}
                size="small"
              />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {theme.published_at
                ? `${t('labels.themes.publishedOn')}: ${new Date(theme.published_at).toLocaleDateString()}`
                : `${t('labels.themes.updatedOn')}: ${new Date(theme.updated_at).toLocaleDateString()}`
              }
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {theme.status !== 'archived' && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon fontSize="small" />}
                onClick={() => theme.status === 'published'
                  ? handleEditLive(theme.id)
                  : history.push(`/admin/themes/${theme.id}/editor`)
                }
              >
                {t('labels.themes.edit')}
              </Button>
            )}

            <Tooltip title={t('labels.themes.moreActions')}>
              <IconButton
                size="small"
                onClick={(e) => openMenu(e, theme.id)}
                aria-label={t('labels.themes.moreActions')}
                aria-haspopup="menu"
              >
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  )

  return (
    <Box sx={{ p: 3, maxWidth: 800 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>{t('labels.themes.storefront')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        {t('labels.themes.storefrontDescription')}
      </Typography>

      {loading && <Typography>{t('labels.themes.loading')}</Typography>}

      <Box sx={{ mb: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mb: 1.5 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t('labels.themes.liveTheme')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('labels.themes.liveThemeDescription')}
            </Typography>
          </Box>
        </Stack>
        {published.length > 0 ? (
          <Stack spacing={2}>
            {published.map(renderThemeCard)}
          </Stack>
        ) : (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                {t('labels.themes.noLiveTheme')}
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>

      <Box sx={{ mb: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mb: 1.5 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t('labels.themes.drafts')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('labels.themes.draftsDescription')}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            {t('labels.themes.createTheme')}
          </Button>
        </Stack>
        {drafts.length > 0 ? (
          <Stack spacing={2}>
            {drafts.map(renderThemeCard)}
          </Stack>
        ) : (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                {t('labels.themes.noDrafts')}
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>

      {archived.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t('labels.themes.archived')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('labels.themes.archivedDescription')}
            </Typography>
          </Box>
          <Stack spacing={2}>
            {archived.map(renderThemeCard)}
          </Stack>
        </Box>
      )}

      <Menu
        anchorEl={menuAnchor.el}
        open={!!menuAnchor.el}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {(() => {
          const theme = themes.find(t => t.id === menuAnchor.themeId)
          if (!theme) return null
          const items = []

          if (theme.status === 'draft') {
            items.push(
              <MenuItem key="publish" onClick={() => { setPublishConfirm(theme.id); closeMenu() }}>
                <ListItemIcon><PublishIcon fontSize="small" /></ListItemIcon>
                <ListItemText>{t('labels.themes.publish')}</ListItemText>
              </MenuItem>
            )
          }

          if (theme.status === 'published') {
            items.push(
              <MenuItem key="unpublish" onClick={() => { setUnpublishConfirm(theme.id); closeMenu() }}>
                <ListItemIcon><UnpublishedIcon fontSize="small" /></ListItemIcon>
                <ListItemText>{t('labels.themes.unpublish')}</ListItemText>
              </MenuItem>
            )
          }

          items.push(
            <MenuItem key="duplicate" onClick={() => {
              setCloneFrom(theme.id)
              setNewName(`${theme.name} (${t('labels.themes.copy')})`)
              setCreateOpen(true)
              closeMenu()
            }}>
              <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{t('labels.themes.duplicate')}</ListItemText>
            </MenuItem>
          )

          if (theme.status !== 'published') {
            items.push(
              <MenuItem key="delete" onClick={() => { setDeleteConfirm(theme.id); closeMenu() }} sx={{ color: 'error.main' }}>
                <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                <ListItemText>{t('labels.themes.delete')}</ListItemText>
              </MenuItem>
            )
          }

          return items
        })()}
      </Menu>

      <Dialog
        open={createOpen}
        onClose={handleCloseCreateDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack spacing={0.5}>
            <Typography variant="h6">
              {cloneFrom !== BLANK_THEME ? t('labels.themes.duplicateTheme') : t('labels.themes.createTheme')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {cloneFrom !== BLANK_THEME
                ? t('labels.themes.duplicateThemeDescription')
                : t('labels.themes.createThemeDescription')}
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={3}>
            <Box
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                backgroundColor: '#f8fafc',
              }}
            >
              <Stack spacing={2}>
                <TextField
                  autoFocus
                  label={t('labels.themes.themeName')}
                  placeholder={t('labels.themes.themeNamePlaceholder')}
                  fullWidth
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  helperText={t('labels.themes.themeNameHelper')}
                />

                <FormControl fullWidth size="small">
                  <InputLabel>{t('labels.themes.startingPoint')}</InputLabel>
                  <Select
                    value={cloneFrom}
                    label={t('labels.themes.startingPoint')}
                    onChange={(e) => setCloneFrom(e.target.value)}
                  >
                    <MenuItem value={BLANK_THEME}>
                      {t('labels.themes.blankTheme')}
                    </MenuItem>

                    {themes.map((theme) => (
                      <MenuItem key={theme.id} value={theme.id}>
                        {theme.name} ({getStatusLabel(theme.status, t)})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Box>

            <Box
              sx={{
                p: 2,
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 2,
              }}
            >
              <Stack spacing={1}>
                <Typography variant="subtitle2">
                  {t('labels.themes.whatHappensNext')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('labels.themes.whatHappensNextDescription')}
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseCreateDialog}>
            {t('labels.themes.cancel')}
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={!newName.trim()}
          >
            {cloneFrom !== BLANK_THEME ? t('labels.themes.duplicateTheme') : t('labels.themes.create')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!publishConfirm} onClose={() => setPublishConfirm(null)}>
        <DialogTitle>{t('labels.themes.publishTheme')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('labels.themes.publishThemeDescription')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishConfirm(null)}>{t('labels.themes.cancel')}</Button>
          <Button onClick={() => handlePublish(publishConfirm)} variant="contained" color="primary">{t('labels.themes.publish')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>{t('labels.themes.deleteTheme')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('labels.themes.deleteThemeDescription')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>{t('labels.themes.cancel')}</Button>
          <Button onClick={() => handleDelete(deleteConfirm)} variant="contained" color="error">{t('labels.themes.delete')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!unpublishConfirm} onClose={() => setUnpublishConfirm(null)}>
        <DialogTitle>{t('labels.themes.unpublishTheme')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('labels.themes.unpublishThemeDescription')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnpublishConfirm(null)}>{t('labels.themes.cancel')}</Button>
          <Button onClick={() => handleUnpublish(unpublishConfirm)} variant="contained" color="primary">
            {t('labels.themes.unpublish')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}